// ShadcnDialogの型定義
interface ShadcnDialog {
  closeAll: (force?: boolean) => void;
  close: (id: string, force?: boolean) => void;
  open: (id: string) => void;
}

class RemoteModal {
  private readonly MODAL_TRIGGER_SELECTOR = '.js-remote-modal-trigger';
  private readonly MEMO_FORM_SELECTOR = '.js-memo-form';

  constructor() {
    this.init();
  }

  private init(): void {
    this.addEvents();
  }

  private addEvents(): void {
    // リンクのクリックイベントをインターセプト
    document.addEventListener('click', this.handleLinkClick.bind(this));
    // フォームの送信イベントをインターセプト
    document.addEventListener('submit', this.handleFormSubmit.bind(this));
    // メモフォームの送信イベントをインターセプト
    document.addEventListener('submit', this.handleMemoFormSubmit.bind(this));
  }

  private async handleLinkClick(event: Event): Promise<void> {
    const target = event.target as HTMLElement;
    const link = target.closest(
      `a${this.MODAL_TRIGGER_SELECTOR}`
    ) as HTMLAnchorElement | null;

    if (!link) return;

    event.preventDefault();
    event.stopPropagation();

    const url = link.href;
    if (!url) return;

    this.disableElement(link);

    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          Accept: 'text/html',
          'X-Requested-With': 'XMLHttpRequest',
        },
      });

      if (response.ok) {
        const html = await response.text();
        this.settingAndShowModal(html);
      }
    } finally {
      this.enableElement(link);
    }
  }

  private async handleFormSubmit(event: Event): Promise<void> {
    const target = event.target as HTMLElement;
    const form = target.closest(
      `form${this.MODAL_TRIGGER_SELECTOR}`
    ) as HTMLFormElement | null;

    if (!form) return;

    event.preventDefault();
    event.stopPropagation();

    const url = form.action;
    const method = form.method.toUpperCase() || 'GET';
    const formData = new FormData(form);

    const submitButton = form.querySelector(
      'button[type="submit"], input[type="submit"]'
    ) as HTMLButtonElement | HTMLInputElement | null;
    if (submitButton) {
      this.disableElement(submitButton);
    }

    try {
      let response: Response;

      if (method === 'GET') {
        const params = new URLSearchParams();
        formData.forEach((value, key) => {
          params.append(key, value.toString());
        });
        const urlWithParams = `${url}?${params.toString()}`;
        response = await fetch(urlWithParams, {
          method: 'GET',
          headers: {
            Accept: 'text/html',
            'X-Requested-With': 'XMLHttpRequest',
          },
        });
      } else {
        response = await fetch(url, {
          method: method,
          headers: {
            Accept: 'text/html',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: formData,
        });
      }

      if (response.ok) {
        const html = await response.text();
        this.settingAndShowModal(html);
      }
    } finally {
      if (submitButton) {
        this.enableElement(submitButton);
      }
    }
  }

  private async handleMemoFormSubmit(event: Event): Promise<void> {
    const target = event.target as HTMLElement;
    const form = target.closest(
      `form${this.MEMO_FORM_SELECTOR}`
    ) as HTMLFormElement | null;

    if (!form) return;

    event.preventDefault();
    event.stopPropagation();

    const url = form.action;
    const formData = new FormData(form);

    const submitButton = form.querySelector(
      'button[type="submit"], input[type="submit"]'
    ) as HTMLButtonElement | HTMLInputElement | null;
    if (submitButton) {
      this.disableElement(submitButton);
    }

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: formData,
      });

      if (response.ok) {
        const data = await response.json();
        // モーダルを閉じる
        const shadcnDialog = (
          window as Window & { ShadcnDialog?: ShadcnDialog }
        ).ShadcnDialog;
        shadcnDialog?.close('modal', true);

        // メモ表示を更新
        const memoElement = document.getElementById(
          `memo_applicant${data.applicant_id}`
        );
        if (memoElement) {
          memoElement.textContent = data.memo_content;
        }
      }
    } finally {
      if (submitButton) {
        this.enableElement(submitButton);
      }
    }
  }

  private settingAndShowModal(modalHtml: string): void {
    // 既存のDialogを閉じて削除
    const shadcnDialog = (window as Window & { ShadcnDialog?: ShadcnDialog })
      .ShadcnDialog;
    shadcnDialog?.closeAll(true);

    // HTMLを一時的なコンテナに挿入
    const tempContainer = document.createElement('div');
    tempContainer.innerHTML = modalHtml;
    const modalElement = tempContainer.firstElementChild as HTMLElement;

    if (!modalElement) return;

    document.body.appendChild(modalElement);

    // open
    const id = modalElement.id;
    if (id) {
      shadcnDialog?.open(id);
    }

    // 閉じたらDOMを削除
    modalElement.addEventListener(
      'shadcn:dialog:closed',
      () => {
        modalElement.remove();
      },
      { once: true }
    );
  }

  private disableElement(element: HTMLElement): void {
    if (
      element instanceof HTMLButtonElement ||
      element instanceof HTMLInputElement
    ) {
      element.disabled = true;
    } else if (element instanceof HTMLAnchorElement) {
      element.style.pointerEvents = 'none';
      element.setAttribute('data-disabled', 'true');
    }
  }

  private enableElement(element: HTMLElement): void {
    if (
      element instanceof HTMLButtonElement ||
      element instanceof HTMLInputElement
    ) {
      element.disabled = false;
    } else if (element instanceof HTMLAnchorElement) {
      element.style.pointerEvents = '';
      element.removeAttribute('data-disabled');
    }
  }
}

// シングルトンインスタンスを保持
let remoteModalInstance: RemoteModal | null = null;

// 初期化関数
function initRemoteModal(): void {
  // 既にインスタンスがある場合は何もしない（イベントリスナーの重複防止）
  if (remoteModalInstance) return;
  remoteModalInstance = new RemoteModal();
}

// 初期ロード時とTurbo遷移時の両方に対応
document.addEventListener('turbo:load', initRemoteModal);

// スクリプトが遅延読み込みされた場合、既にDOMが読み込み済みなら即座に実行
if (document.readyState !== 'loading') {
  initRemoteModal();
}

// モジュールとして認識させるためのexport
export {};
